import { ToolbarItem } from 'components/layout/Main/types';
import { ADMIN_ACCESS_LEVELS, SECURITY_USER } from './value';

/**
 * The header nav, built in one place so every screen shows the same links for a
 * given role. It used to be copy-pasted into each page, which is how normal
 * users ended up with a Dashboard link pointing at the security dashboard (a
 * page their role cannot open) and no Sites link at all — even though /sites is
 * open to them.
 *
 * Visibility per role:
 *  - Dashboard: everyone. Security lands on /security-dashboard, everyone else
 *    on /dashboard — those are the only routes each role is allowed to open.
 *  - Unreturned Cards & Keys: security only.
 *  - Useful Contacts: admins only (the route is admin-only).
 *  - Sites: everyone except security, whose nav is unchanged.
 *  - Incidents: admins only (the route excludes normal users).
 */

interface MainToolbarOptions {
    /**
     * Link to highlight when the current screen is not that item's own route —
     * e.g. site-scoped Incidents (/site/:id/incidents) highlights Sites.
     */
    activeLink?: string;
}

/**
 * The dashboard each role is allowed to open. Security has its own dashboard;
 * every other role (including normal users) uses /dashboard.
 */
export const dashboardLinkFor = (accessLevel?: string): string => (
    accessLevel === SECURITY_USER ? '/security-dashboard' : '/dashboard'
);

export const buildMainToolbar = (
    accessLevel?: string,
    options: MainToolbarOptions = {},
): ToolbarItem[] => {
    const isAdmin = !!accessLevel && ADMIN_ACCESS_LEVELS.includes(accessLevel);
    const isSecurity = accessLevel === SECURITY_USER;

    const items: ToolbarItem[] = [
        { label: 'Dashboard', link: dashboardLinkFor(accessLevel), iconName: 'home', tourId: 'nav-dashboard' },
        ...(isSecurity
            ? [{
                label: 'Unreturned Cards & Keys',
                link: '/unreturned-cards-and-keys',
                iconName: 'key',
                tourId: 'nav-unreturned',
            }]
            : []),
        ...(isAdmin
            ? [{ label: 'Useful Contacts', link: '/useful-contacts', iconName: 'contacts', tourId: 'nav-useful-contacts' }]
            : []),
        ...(!isSecurity
            ? [{ label: 'Sites', link: '/sites', iconName: 'site', tourId: 'nav-sites' }]
            : []),
        ...(isAdmin
            ? [{ label: 'Incidents', link: '/incidents', iconName: 'incident', tourId: 'nav-incidents' }]
            : []),
    ];

    if (!options.activeLink) return items;

    return items.map((item) => (
        item.link === options.activeLink ? { ...item, isActive: true } : item
    ));
};

export default buildMainToolbar;
